import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Users,
  Award,
  Clock,
  CheckCircle,
  BarChart3,
  PieChart,
  Calendar,
  ArrowRight,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader, MetricCard } from '@/components/common';
import { toast } from 'sonner';
import { rfqApi, RFQAnalyticsData, RFQAnalyticsFilter } from '@/api/services';
import { getErrorMessage } from '@/api/client';

type DateRangeOption = NonNullable<RFQAnalyticsFilter['dateRange']>;

export default function FreightRFQAnalytics() {
  const [dateRange, setDateRange] = useState<DateRangeOption>('6m');
  const [data, setData] = useState<RFQAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const analyticsData = await rfqApi.getRFQAnalytics({ dateRange });
      setData(analyticsData);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="RFQ Analytics"
          description="Insights and performance metrics for freight procurement"
        />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="RFQ Analytics"
          description="Insights and performance metrics for freight procurement"
        />
        <div className="flex flex-col items-center justify-center h-64">
          <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
          <h3 className="text-lg font-medium mb-2">Failed to Load Analytics</h3>
          <p className="text-muted-foreground mb-4">{error || 'Unable to fetch analytics data'}</p>
          <Button onClick={loadAnalytics}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="RFQ Analytics"
        description="Insights and performance metrics for freight procurement"
        actions={
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRangeOption)}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1m">Last Month</SelectItem>
                <SelectItem value="3m">Last 3 Months</SelectItem>
                <SelectItem value="6m">Last 6 Months</SelectItem>
                <SelectItem value="1y">Last Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>
        }
      />

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard
          label="Total RFQs"
          value={data.summary.totalRFQs}
          icon={<FileText className="w-5 h-5" />}
        />
        <MetricCard
          label="Open RFQs"
          value={data.summary.openRFQs}
          icon={<Clock className="w-5 h-5 text-amber-500" />}
        />
        <MetricCard
          label="Awarded"
          value={data.summary.awardedRFQs}
          icon={<Award className="w-5 h-5 text-emerald-500" />}
        />
        <MetricCard
          label="Total Value"
          value={formatCurrency(data.summary.totalValue)}
          icon={<DollarSign className="w-5 h-5" />}
        />
        <MetricCard
          label="Avg. Time to Award"
          value={`${data.summary.avgTimeToAward} days`}
          icon={<Calendar className="w-5 h-5" />}
        />
        <MetricCard
          label="Avg. Savings"
          value={`${data.summary.avgSavingsPercent}%`}
          icon={<TrendingDown className="w-5 h-5 text-emerald-500" />}
        />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="vendors">Vendor Analysis</TabsTrigger>
          <TabsTrigger value="lanes">Lane Analysis</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab data={data} formatCurrency={formatCurrency} />
        </TabsContent>
        <TabsContent value="vendors" className="mt-6">
          <VendorsTab data={data} formatCurrency={formatCurrency} />
        </TabsContent>
        <TabsContent value="lanes" className="mt-6">
          <LanesTab data={data} formatCurrency={formatCurrency} />
        </TabsContent>
        <TabsContent value="trends" className="mt-6">
          <TrendsTab data={data} formatCurrency={formatCurrency} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewTab({
  data,
  formatCurrency,
}: {
  data: RFQAnalyticsData;
  formatCurrency: (v: number) => string;
}) {
  const totalCount = data.statusBreakdown.reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="space-y-8">
      {/* Status Breakdown */}
      <div className="bg-card rounded-xl border p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <PieChart className="w-5 h-5 text-muted-foreground" />
          RFQ Status Distribution
        </h3>
        <div className="grid grid-cols-4 gap-4">
          {data.statusBreakdown.map((status) => {
            const percentage = ((status.count / totalCount) * 100).toFixed(1);
            const colors: Record<string, string> = {
              Draft: 'bg-muted border-muted-foreground/20',
              Open: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800',
              Closed:
                'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
              Awarded:
                'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800',
            };

            return (
              <div
                key={status.status}
                className={`p-4 rounded-xl border ${colors[status.status] || 'bg-muted'}`}
              >
                <div className="text-sm text-muted-foreground mb-1">
                  {status.status}
                </div>
                <div className="text-2xl font-bold">{status.count}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">
                    {percentage}%
                  </span>
                  <span className="text-sm font-medium">
                    {formatCurrency(status.value)}
                  </span>
                </div>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary/30"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Time to Award */}
      <div className="bg-card rounded-xl border p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-muted-foreground" />
          Time to Award Distribution
        </h3>
        <div className="flex items-end gap-2 h-40">
          {data.timeToAwardDistribution.map((item) => {
            const maxCount = Math.max(
              ...data.timeToAwardDistribution.map((d) => d.count)
            );
            const height = (item.count / maxCount) * 100;

            return (
              <div key={item.range} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-primary/20 rounded-t-lg transition-all hover:bg-primary/30"
                  style={{ height: `${height}%` }}
                />
                <div className="mt-2 text-xs text-muted-foreground text-center">
                  {item.range}
                </div>
                <div className="text-sm font-medium">{item.count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Award Rate</span>
          </div>
          <div className="text-2xl font-bold">
            {((data.summary.awardedRFQs / data.summary.totalRFQs) * 100).toFixed(
              1
            )}
            %
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {data.summary.awardedRFQs} of {data.summary.totalRFQs} RFQs awarded
          </div>
        </div>

        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <Users className="w-4 h-4" />
            <span className="text-sm">Avg. Vendors per RFQ</span>
          </div>
          <div className="text-2xl font-bold">4.2</div>
          <div className="text-xs text-muted-foreground mt-1">
            Invited carriers average
          </div>
        </div>

        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Response Rate</span>
          </div>
          <div className="text-2xl font-bold">87.5%</div>
          <div className="text-xs text-muted-foreground mt-1">
            Carriers responding to invitations
          </div>
        </div>
      </div>
    </div>
  );
}

function VendorsTab({
  data,
  formatCurrency,
}: {
  data: RFQAnalyticsData;
  formatCurrency: (v: number) => string;
}) {
  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-muted-foreground" />
            Vendor Participation Analysis
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">
                  Carrier
                </th>
                <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                  Invitations
                </th>
                <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                  Responded
                </th>
                <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                  Response Rate
                </th>
                <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                  Awards
                </th>
                <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                  Win Rate
                </th>
                <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                  Total Value
                </th>
              </tr>
            </thead>
            <tbody>
              {data.vendorParticipation.map((vendor, idx) => {
                const responseRate = (
                  (vendor.responded / vendor.invitations) *
                  100
                ).toFixed(1);
                const winRate = (
                  (vendor.awarded / vendor.responded) *
                  100
                ).toFixed(1);

                return (
                  <tr key={vendor.carrierId} className="border-b hover:bg-muted/50">
                    <td className="py-3 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center text-sm font-medium">
                          {idx + 1}
                        </div>
                        <span className="font-medium">{vendor.carrierName}</span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-6">
                      {vendor.invitations}
                    </td>
                    <td className="text-right py-3 px-6">{vendor.responded}</td>
                    <td className="text-right py-3 px-6">
                      <span
                        className={`px-2 py-0.5 rounded text-sm ${
                          parseFloat(responseRate) >= 90
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : parseFloat(responseRate) >= 70
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}
                      >
                        {responseRate}%
                      </span>
                    </td>
                    <td className="text-right py-3 px-6 font-medium">
                      {vendor.awarded}
                    </td>
                    <td className="text-right py-3 px-6">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded text-sm">
                        {winRate}%
                      </span>
                    </td>
                    <td className="text-right py-3 px-6 font-medium">
                      {formatCurrency(vendor.totalValue)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Visual Comparison */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-card rounded-xl border p-6">
          <h4 className="font-medium mb-4">Awards by Carrier</h4>
          <div className="space-y-3">
            {data.vendorParticipation.slice(0, 5).map((vendor) => {
              const maxAwards = Math.max(
                ...data.vendorParticipation.map((v) => v.awarded)
              );
              const width = (vendor.awarded / maxAwards) * 100;

              return (
                <div key={vendor.carrierId}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{vendor.carrierName}</span>
                    <span className="font-medium">{vendor.awarded}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card rounded-xl border p-6">
          <h4 className="font-medium mb-4">Award Value by Carrier</h4>
          <div className="space-y-3">
            {data.vendorParticipation.slice(0, 5).map((vendor) => {
              const maxValue = Math.max(
                ...data.vendorParticipation.map((v) => v.totalValue)
              );
              const width = (vendor.totalValue / maxValue) * 100;

              return (
                <div key={vendor.carrierId}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{vendor.carrierName}</span>
                    <span className="font-medium">
                      {formatCurrency(vendor.totalValue)}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function LanesTab({
  data,
  formatCurrency,
}: {
  data: RFQAnalyticsData;
  formatCurrency: (v: number) => string;
}) {
  return (
    <div className="bg-card rounded-xl border overflow-hidden">
      <div className="px-6 py-4 border-b">
        <h3 className="font-semibold flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-muted-foreground" />
          Top Lanes by RFQ Activity
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">
                Lane
              </th>
              <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                RFQ Count
              </th>
              <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                Total Value
              </th>
              <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                Avg. Rate
              </th>
              <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                Share
              </th>
            </tr>
          </thead>
          <tbody>
            {data.topLanes.map((lane, idx) => {
              const totalValue = data.topLanes.reduce(
                (sum, l) => sum + l.totalValue,
                0
              );
              const share = ((lane.totalValue / totalValue) * 100).toFixed(1);

              return (
                <tr key={idx} className="border-b hover:bg-muted/50">
                  <td className="py-3 px-6">
                    <div className="flex items-center gap-2">
                      <div>
                        <span className="font-medium">{lane.originCity}</span>
                        <span className="text-muted-foreground text-xs ml-1">({lane.originCountry})</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <span className="font-medium">{lane.destCity}</span>
                        <span className="text-muted-foreground text-xs ml-1">({lane.destCountry})</span>
                      </div>
                    </div>
                  </td>
                  <td className="text-right py-3 px-6">{lane.rfqCount}</td>
                  <td className="text-right py-3 px-6 font-medium">
                    {formatCurrency(lane.totalValue)}
                  </td>
                  <td className="text-right py-3 px-6">
                    {formatCurrency(lane.avgRate)}
                  </td>
                  <td className="text-right py-3 px-6">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${share}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {share}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrendsTab({
  data,
  formatCurrency,
}: {
  data: RFQAnalyticsData;
  formatCurrency: (v: number) => string;
}) {
  const maxCount = Math.max(...data.monthlyTrends.map((m) => m.rfqCount));

  return (
    <div className="space-y-8">
      <div className="bg-card rounded-xl border p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-muted-foreground" />
          Monthly RFQ Trends
        </h3>

        {/* Chart */}
        <div className="h-64 flex items-end gap-4">
          {data.monthlyTrends.map((month) => {
            const countHeight = (month.rfqCount / maxCount) * 100;

            return (
              <div key={month.month} className="flex-1 flex flex-col items-center">
                <div className="w-full flex items-end justify-center gap-1 h-48">
                  <div
                    className="w-6 bg-blue-200 dark:bg-blue-800 rounded-t"
                    style={{ height: `${countHeight}%` }}
                    title={`RFQs: ${month.rfqCount}`}
                  />
                  <div
                    className="w-6 bg-emerald-200 dark:bg-emerald-800 rounded-t"
                    style={{ height: `${(month.awardedCount / maxCount) * 100}%` }}
                    title={`Awarded: ${month.awardedCount}`}
                  />
                </div>
                <div className="mt-2 text-xs text-muted-foreground text-center">
                  {month.month.split(' ')[0]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-200 dark:bg-blue-800 rounded" />
            <span className="text-sm text-muted-foreground">Total RFQs</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-200 dark:bg-emerald-800 rounded" />
            <span className="text-sm text-muted-foreground">Awarded</span>
          </div>
        </div>
      </div>

      {/* Monthly Stats Table */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h4 className="font-medium">Monthly Breakdown</h4>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">
                Month
              </th>
              <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                RFQs
              </th>
              <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                Awarded
              </th>
              <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                Award Rate
              </th>
              <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">
                Total Value
              </th>
            </tr>
          </thead>
          <tbody>
            {data.monthlyTrends.map((month) => {
              const awardRate = (
                (month.awardedCount / month.rfqCount) *
                100
              ).toFixed(1);

              return (
                <tr key={month.month} className="border-b hover:bg-muted/50">
                  <td className="py-3 px-6 font-medium">{month.month}</td>
                  <td className="text-right py-3 px-6">{month.rfqCount}</td>
                  <td className="text-right py-3 px-6">{month.awardedCount}</td>
                  <td className="text-right py-3 px-6">
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded text-sm">
                      {awardRate}%
                    </span>
                  </td>
                  <td className="text-right py-3 px-6 font-medium">
                    {formatCurrency(month.totalValue)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
